import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../../lib/api';
import { useOrg } from '../../../hooks/useOrg';
import ConfirmDialog from '../../shared/ConfirmDialog';

export default function IntakeFormsTab() {
  const { currentOrg } = useOrg();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', url: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchForms = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const data = await api.getIntakeForms(currentOrg.id);
      setForms(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  useEffect(() => { fetchForms(); }, [fetchForms]);

  const resetForm = () => { setForm({ name: '', url: '' }); setShowAdd(false); setEditId(null); setError(''); };

  const handleSave = async () => {
    if (!form.name.trim() || !form.url.trim()) { setError('Name and URL are required'); return; }
    if (!form.url.startsWith('http')) { setError('URL must start with http:// or https://'); return; }
    setSaving(true);
    setError('');
    try {
      if (editId) {
        await api.updateIntakeForm(currentOrg.id, editId, form);
      } else {
        await api.createIntakeForm(currentOrg.id, form);
      }
      resetForm();
      fetchForms();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.deleteIntakeForm(currentOrg.id, confirmDelete.id);
      setConfirmDelete(null);
      fetchForms();
    } catch (err) {
      console.error(err);
    }
  };

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-[15px] font-bold text-primary-c">Intake Forms</h3>
          <p className="text-[12px] text-muted-c mt-0.5">Add links to your intake forms (DocuSign, JotForm, PandaDoc, etc). These are sent to clients when they're onboarded.</p>
        </div>
        {!showAdd && (
          <button onClick={() => { setShowAdd(true); setEditId(null); setForm({ name: '', url: '' }); }}
            className="px-4 py-2 rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all">
            + Add Form
          </button>
        )}
      </div>

      {/* Add/Edit form */}
      {showAdd && (
        <div className="bg-tertiary rounded-xl border border-theme p-5 mb-4 animate-fade-in">
          <h4 className="text-[13px] font-semibold text-primary-c mb-3">{editId ? 'Edit Form' : 'Add Intake Form'}</h4>
          {error && <div className="px-4 py-2.5 rounded-[10px] bg-danger-soft text-danger text-[12px] font-medium mb-3">{error}</div>}
          <div className="space-y-3">
            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-1.5">Form Name</label>
              <input type="text" className={inputClass} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., New Patient Intake Form" />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-1.5">Form URL</label>
              <input type="url" className={inputClass} value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} placeholder="https://app.docusign.com/..." />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={resetForm} className="px-4 py-2 rounded-[10px] text-[12px] font-semibold text-primary-c bg-elevated border border-theme hover:bg-hover-surface transition-all">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow transition-all disabled:opacity-50">
              {saving ? 'Saving...' : editId ? 'Save Changes' : 'Add Form'}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="space-y-2">
          {[1,2].map(i => <div key={i} className="h-16 bg-tertiary rounded-xl animate-pulse" />)}
        </div>
      ) : forms.length === 0 ? (
        <div className="text-center py-10 bg-tertiary rounded-xl border border-theme">
          <p className="text-[13px] text-muted-c">No intake forms configured yet</p>
          <p className="text-[11px] text-muted-c mt-1">Add your first form to start sending them to clients</p>
        </div>
      ) : (
        <div className="space-y-2">
          {forms.map(f => (
            <div key={f.id} className="flex items-center gap-3 p-4 bg-elevated rounded-xl border border-theme group">
              <div className="w-10 h-10 rounded-[10px] bg-accent-glow flex items-center justify-center flex-shrink-0">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0DA7CA" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-primary-c">{f.name}</div>
                <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-accent-light hover:underline truncate block">{f.url}</a>
              </div>
              <div className="flex gap-2 shrink-0 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                <button onClick={() => { setEditId(f.id); setForm({ name: f.name, url: f.url }); setShowAdd(true); }}
                  className="text-[11px] font-medium text-muted-c hover:text-accent-light transition-colors">Edit</button>
                <button onClick={() => setConfirmDelete(f)}
                  className="text-[11px] font-medium text-muted-c hover:text-danger transition-colors">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Intake Form"
        message={`Remove "${confirmDelete?.name}"? Existing client assignments will be kept.`}
        confirmLabel="Delete"
        danger
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
